import React from 'react'
import {
  Route,
  Routes,
  useLocation,
  Navigate,
  useParams,
  useNavigate,
} from 'react-router-dom'
import useModals from '@/v2/hooks/useModals'
import useDetectDevice from '@/v2/hooks/useDetectDevice'
import SeatPatient from '@/v2/modals/check-in'
import Event from '@/v2/modals/event'
import AppointmentDetails from '@/v2/modals/appointment/AppointmentDetails'
import AppointmentEdit from '@/v2/modals/appointment/AppointmentEdit'
import TimeOffEdit from '@/v2/modals/time-off/Edit'
import NewPatient from '@/v2/modals/new-patient'
import CreateContract from '@/v2/modals/patient-documents/CreateContract'
import ContractDetails from '@/v2/modals/patient-documents/ContractDetails'
import ConsentFormDetails from '@/v2/modals/patient-documents/ConsentFormDetails'
import ConsentFormConfirmation from '@/v2/modals/patient-documents/ConsentFormConfirmation'
import ManageCards from '@/v2/modals/patient-finance/ManageCards'
import ManageAccounts from '@/v2/modals/patient-finance/ManageAccounts'
import Receipts from '@/v2/modals/patient-finance/Receipts'
import CreatePaymentPlans from '@/v2/modals/patient-finance/CreatePaymentPlans'
import RefundPayment from '@/v2/modals/patient-finance/RefundPayment'
import DeclinePayment from '@/v2/modals/patient-finance/DeclinePayment'
import NewPatientNotes from '@/v2/modals/new-patient-note'
import EditPatientNotes from '@/v2/modals/edit-patient-note'
import EditPatientBasicInfo from '@/v2/modals/edit-patient-basic-info'
import EditPatientContactInfo from '@/v2/modals/edit-patient-contact-info'
import EditPatientEmergencyContact from '@/v2/modals/edit-patient-emergency-contact'
import CropImage from '@/v2/modals/patient-imaging/CropImage'
import MedicalAlerts from '@/v2/modals/edit-patient-medical-alerts'
import { CreatePaymentProvider } from '@/v2/modals/patient-finance/CreatePaymentPlans/components/CreatePaymentStateContext'
import ChangePaymentMethod from '@/v2/modals/patient-finance/ChangePaymentMethod'
import AdvancedPayment from '@/v2/modals/patient-finance/AdvancedPayment'
import EditInvoiceDueDate from '@/v2/modals/patient-finance/EditInvoiceDueDate'
import { CreateContractProvider } from '@/v2/modals/patient-documents/CreateContract/CreateContractStateContext'
import InsuranceForm from '@/v2/modals/insurance'
import EligibilityForm from '@/v2/modals/eligibility'
import ClaimForm from '@/v2/modals/claim'
import TaskForm from '@/v2/modals/task'
import { readClaimIdParam } from '@/v2/utils/claimDeepLink'

const EventWrapper = ({ isOpen, onClose }) => {
  const location = useLocation()
  const params = useParams()
  const { isMobile } = useDetectDevice()

  // On mobile, don't render the modal - let MobileMainRoutes handle it
  if (isMobile) {
    return null
  }

  return (
    <Event
      isOpen={isOpen}
      onClose={onClose}
      {...params}
      {...(location.state?.initialValues || {})}
      {...location.state?.onCreate}
    />
  )
}

const AppointmentEditWrapper = ({ isOpen, onClose }) => {
  const location = useLocation()
  const params = useParams()
  const { isMobile } = useDetectDevice()

  // On mobile, don't render the modal - let MobileMainRoutes handle it
  if (isMobile) {
    return null
  }

  return (
    <AppointmentEdit
      isOpen={isOpen}
      onClose={onClose}
      {...params}
      {...(location.state || {})}
      onEdit={location.state?.onEdit}
      onCancel={location.state?.onCancel}
    />
  )
}

const AppointmentDetailsWrapper = ({ isOpen, onClose }) => {
  const location = useLocation()
  const params = useParams()
  const { isMobile } = useDetectDevice()

  // On mobile, don't render the modal - let MobileMainRoutes handle it
  if (isMobile) {
    return null
  }

  return (
    <AppointmentDetails
      isOpen={isOpen}
      onClose={onClose}
      {...params}
      {...(location.state?.initialValues || {})}
      onEdit={location.state?.onEdit}
      onCancel={location.state?.onCancel}
    />
  )
}

const TimeOffEditWrapper = ({ isOpen, onClose }) => {
  const location = useLocation()
  const params = useParams()

  return (
    <TimeOffEdit
      isOpen={isOpen}
      onClose={onClose}
      {...params}
      {...(location.state?.initialValues || {})}
    />
  )
}

const NewPatientWrapper = ({ isOpen, onClose }) => {
  const location = useLocation()
  const params = useParams()

  return (
    <NewPatient
      isOpen={isOpen}
      onClose={onClose}
      {...params}
      {...(location.state?.initialValues || {})}
      refetch={location.state?.refetch}
    />
  )
}

const SeatPatientWrapper = ({ isOpen, onClose }) => {
  const params = useParams()

  return <SeatPatient {...params} isOpen={isOpen} onClose={onClose} />
}

const CreateContractWrapper = ({ isOpen, onClose }) => {
  const location = useLocation()
  const params = useParams()

  return (
    <CreateContractProvider>
      <CreateContract
        isOpen={isOpen}
        onClose={onClose}
        {...params}
        {...(location.state?.initialValues || {})}
      />
    </CreateContractProvider>
  )
}

const ContractDetailsWrapper = ({ isOpen, onClose }) => {
  const location = useLocation()
  const params = useParams()

  return (
    <ContractDetails
      isOpen={isOpen}
      onClose={onClose}
      {...params}
      {...(location.state?.initialValues || {})}
    />
  )
}

const ConsentFormDetailsWrapper = ({ isOpen, onClose }) => {
  const location = useLocation()
  const params = useParams()

  return (
    <ConsentFormDetails
      isOpen={isOpen}
      onClose={onClose}
      {...params}
      {...(location.state || {})}
    />
  )
}

const ConsentFormConfirmationWrapper = ({ isOpen, onClose }) => {
  const location = useLocation()
  const params = useParams()

  return (
    <ConsentFormConfirmation
      isOpen={isOpen}
      onClose={onClose}
      {...params}
      {...(location.state || {})}
    />
  )
}

const ManageAccountsWrapper = ({ isOpen, onClose }) => {
  const location = useLocation()

  return (
    <ManageAccounts
      isOpen={isOpen}
      onClose={onClose}
      {...(location.state?.initialValues || {})}
    />
  )
}

const ManageCardsWrapper = ({ isOpen, onClose }) => {
  const location = useLocation()

  return (
    <ManageCards
      isOpen={isOpen}
      onClose={onClose}
      {...(location.state?.initialValues || {})}
    />
  )
}

const ReceiptsWrapper = ({ isOpen, onClose }) => {
  const location = useLocation()

  return (
    <Receipts
      isOpen={isOpen}
      onClose={onClose}
      {...(location.state?.initialValues || {})}
    />
  )
}

const CreatePaymentPlansWrapper = ({ isOpen, onClose }) => {
  const location = useLocation()

  return (
    <CreatePaymentProvider>
      <CreatePaymentPlans
        isOpen={isOpen}
        onClose={onClose}
        {...(location.state?.initialValues || {})}
      />
    </CreatePaymentProvider>
  )
}

const RefundPaymentWrapper = ({ isOpen, onClose }) => {
  const location = useLocation()

  return (
    <RefundPayment
      isOpen={isOpen}
      onClose={onClose}
      {...(location.state || {})}
    />
  )
}

const DeclinePaymentWrapper = ({ isOpen, onClose }) => {
  const location = useLocation()

  return (
    <DeclinePayment
      isOpen={isOpen}
      onClose={onClose}
      {...(location.state || {})}
    />
  )
}

const ChangePaymentMethodWrapper = ({ isOpen, onClose }) => {
  const location = useLocation()

  return (
    <ChangePaymentMethod
      isOpen={isOpen}
      onClose={onClose}
      {...(location.state || {})}
    />
  )
}

const AdvancedPaymentWrapper = ({ isOpen, onClose }) => {
  const location = useLocation()

  return (
    <AdvancedPayment
      isOpen={isOpen}
      onClose={onClose}
      {...(location.state || {})}
    />
  )
}

const EditInvoiceDueDateWrapper = ({ isOpen, onClose }) => {
  const location = useLocation()

  return (
    <EditInvoiceDueDate
      isOpen={isOpen}
      onClose={onClose}
      {...(location.state || {})}
    />
  )
}

const MedicalAlertsWrapper = ({ isOpen, onClose }) => {
  const location = useLocation()
  const params = useParams()

  return (
    <MedicalAlerts
      isOpen={isOpen}
      onClose={onClose}
      {...params}
      {...(location.state || {})}
    />
  )
}

const NewPatientNotesWrapper = ({ isOpen, onClose }) => {
  const location = useLocation()
  const params = useParams()

  return (
    <NewPatientNotes
      isOpen={isOpen}
      onClose={onClose}
      {...params}
      {...(location.state || {})}
    />
  )
}

const EditPatientNotesWrapper = ({ isOpen, onClose }) => {
  const location = useLocation()
  const params = useParams()

  return (
    <EditPatientNotes
      isOpen={isOpen}
      onClose={onClose}
      {...params}
      {...(location.state || {})}
    />
  )
}

const EditPatientBasicInfoWrapper = ({ isOpen, onClose }) => {
  const location = useLocation()
  const params = useParams()
  const { isMobile } = useDetectDevice()

  // On mobile, don't render the modal - let MobileMainRoutes handle it
  if (isMobile) {
    return null
  }

  return (
    <EditPatientBasicInfo
      isOpen={isOpen}
      onClose={onClose}
      {...params}
      {...(location.state || {})}
    />
  )
}

const EditPatientContactInfoWrapper = ({ isOpen, onClose }) => {
  const location = useLocation()
  const params = useParams()
  const { isMobile } = useDetectDevice()

  // On mobile, don't render the modal - let MobileMainRoutes handle it
  if (isMobile) {
    return null
  }

  return (
    <EditPatientContactInfo
      isOpen={isOpen}
      onClose={onClose}
      {...params}
      {...(location.state || {})}
    />
  )
}

const EditPatientEmergencyContactWrapper = ({ isOpen, onClose }) => {
  const location = useLocation()
  const params = useParams()

  return (
    <EditPatientEmergencyContact
      isOpen={isOpen}
      onClose={onClose}
      {...params}
      {...(location.state || {})}
    />
  )
}

const CropImageWrapper = ({ isOpen, onClose }) => {
  const location = useLocation()
  const params = useParams()
  const navigate = useNavigate()

  const handleClose = result => {
    // Navigate back with the result in state so parent can handle it
    if (location.state?.backgroundLocation) {
      navigate(location.state.backgroundLocation.pathname, {
        replace: true,
        state: { cropImageResult: result },
      })
    } else {
      onClose()
    }
  }

  return (
    <CropImage
      isOpen={isOpen}
      onClose={handleClose}
      {...params}
      {...(location.state || {})}
    />
  )
}

const InsuranceFormWrapper = ({ isOpen, onClose }) => {
  const location = useLocation()
  const params = useParams()

  return (
    <InsuranceForm
      isOpen={isOpen}
      onClose={onClose}
      {...params}
      {...(location.state || {})}
    />
  )
}

const EligibilityFormWrapper = ({ isOpen, onClose }) => {
  const location = useLocation()
  const params = useParams()

  return (
    <EligibilityForm
      isOpen={isOpen}
      onClose={onClose}
      {...params}
      {...(location.state || {})}
    />
  )
}

const ClaimFormWrapper = ({ isOpen, onClose, isEdit }) => {
  const location = useLocation()
  const params = useParams()

  // The claim modal is driven entirely by navigation state (modalType, claim,
  // backgroundLocation) set when it's opened from within the app. A cold
  // deep-link or hard refresh has no such state, so there's no claim to render
  // and no background page — it would otherwise show an empty modal over the
  // calendar fallback. Redirect to the patient's claims tab; if the URL carries
  // the claimId query param (stamped by history.push), pass it through so the
  // claims tab auto-opens that specific claim once its data has loaded.
  if (!location.state?.modalType) {
    const claimId = readClaimIdParam(location.search)
    return (
      <Navigate
        to={`/v2/patient/${params.id}/insurance`}
        state={{
          openTab: 'claims',
          ...(claimId ? { openClaimId: claimId } : {}),
        }}
        replace
      />
    )
  }

  return (
    <ClaimForm
      key={location.key}
      isEdit={isEdit}
      isOpen={isOpen}
      onClose={onClose}
      {...params}
      {...(location.state || {})}
    />
  )
}

const TaskFormWrapper = ({ isOpen, onClose }) => {
  const location = useLocation()

  return (
    <TaskForm isOpen={isOpen} onClose={onClose} {...(location.state || {})} />
  )
}

const FallbackRoute = () => {
  const location = useLocation()

  React.useEffect(() => {
    if (location.pathname !== '/') {
      window.location.href = location.pathname
    }
  }, [location.pathname])

  if (location.pathname !== '/') {
    return null
  }

  return <Navigate to="/" replace />
}

const ModalRoutes = () => {
  const Modals = useModals()
  const location = useLocation()
  const isOpen = location.pathname && location.pathname !== '/'

  return (
    <Routes>
      <Route
        path="/v2/event/new"
        element={<EventWrapper isOpen={isOpen} onClose={Modals.close} />}
      />
      <Route
        path="/v2/appointment/:id/edit"
        element={
          <AppointmentEditWrapper isOpen={isOpen} onClose={Modals.close} />
        }
      />
      <Route
        path="/v2/appointment/:id"
        element={
          <AppointmentDetailsWrapper isOpen={isOpen} onClose={Modals.close} />
        }
      />
      <Route
        path="/v2/time-off/:id/edit"
        element={<TimeOffEditWrapper isOpen={isOpen} onClose={Modals.close} />}
      />
      <Route
        path="/v2/patient/new"
        element={<NewPatientWrapper isOpen={isOpen} onClose={Modals.close} />}
      />
      <Route
        path="/v2/seat-patient/:appointmentId"
        element={<SeatPatientWrapper isOpen={isOpen} onClose={Modals.close} />}
      />
      <Route
        path="/v2/create-contract/:patientId"
        element={
          <CreateContractWrapper isOpen={isOpen} onClose={Modals.close} />
        }
      />
      <Route
        path="/v2/contract-details/:id"
        element={
          <ContractDetailsWrapper isOpen={isOpen} onClose={Modals.close} />
        }
      />
      <Route
        path="/v2/consent-form-details/:id"
        element={
          <ConsentFormDetailsWrapper isOpen={isOpen} onClose={Modals.close} />
        }
      />
      <Route
        path="/v2/consent-form-confirmation"
        element={
          <ConsentFormConfirmationWrapper
            isOpen={isOpen}
            onClose={Modals.close}
          />
        }
      />
      <Route
        path="/v2/patient/:patientId/finance/manage-accounts/new"
        element={
          <ManageAccountsWrapper isOpen={isOpen} onClose={Modals.close} />
        }
      />
      <Route
        path="/v2/patient/:patientId/finance/manage-cards/new"
        element={<ManageCardsWrapper isOpen={isOpen} onClose={Modals.close} />}
      />
      <Route
        path="/v2/patient/:patientId/finance/receipts"
        element={<ReceiptsWrapper isOpen={isOpen} onClose={Modals.close} />}
      />
      <Route
        path="/v2/patient/:patientId/finance/payment-plan/new"
        element={
          <CreatePaymentPlansWrapper isOpen={isOpen} onClose={Modals.close} />
        }
      />
      <Route
        path="/v2/patient/:patientId/finance/refund-payment"
        element={
          <RefundPaymentWrapper isOpen={isOpen} onClose={Modals.close} />
        }
      />
      <Route
        path="/v2/patient/:patientId/finance/decline-payment"
        element={
          <DeclinePaymentWrapper isOpen={isOpen} onClose={Modals.close} />
        }
      />
      <Route
        path="/v2/patient/:patientId/finance/change-payment-method"
        element={
          <ChangePaymentMethodWrapper isOpen={isOpen} onClose={Modals.close} />
        }
      />
      <Route
        path="/v2/patient/:patientId/finance/advanced-payment"
        element={
          <AdvancedPaymentWrapper isOpen={isOpen} onClose={Modals.close} />
        }
      />
      <Route
        path="/v2/patient/:patientId/finance/edit-invoice-due-date"
        element={
          <EditInvoiceDueDateWrapper isOpen={isOpen} onClose={Modals.close} />
        }
      />
      <Route
        path="/v2/patient/:id/medical-alerts"
        element={
          <MedicalAlertsWrapper isOpen={isOpen} onClose={Modals.close} />
        }
      />
      <Route
        path="/v2/patient/:id/notes/new/:type"
        element={
          <NewPatientNotesWrapper isOpen={isOpen} onClose={Modals.close} />
        }
      />
      <Route
        path="/v2/patient/:id/notes/new"
        element={
          <NewPatientNotesWrapper isOpen={isOpen} onClose={Modals.close} />
        }
      />
      <Route
        path="/v2/patient/:id/notes/:noteId"
        element={
          <EditPatientNotesWrapper isOpen={isOpen} onClose={Modals.close} />
        }
      />
      <Route
        path="/v2/patient/:id/edit/basic"
        element={
          <EditPatientBasicInfoWrapper isOpen={isOpen} onClose={Modals.close} />
        }
      />
      <Route
        path="/v2/patient/:id/edit/contact-info"
        element={
          <EditPatientContactInfoWrapper
            isOpen={isOpen}
            onClose={Modals.close}
          />
        }
      />
      <Route
        path="/v2/patient/:id/edit/emergency-contact"
        element={
          <EditPatientEmergencyContactWrapper
            isOpen={isOpen}
            onClose={Modals.close}
          />
        }
      />
      <Route
        path="/v2/patient/:patientId/crop-image/"
        element={<CropImageWrapper isOpen={isOpen} onClose={Modals.close} />}
      />
      <Route
        path="/v2/patient/:id/insurance/new"
        element={
          <InsuranceFormWrapper isOpen={isOpen} onClose={Modals.close} />
        }
      />
      <Route
        path="/v2/patient/:id/eligibility/check"
        element={
          <EligibilityFormWrapper isOpen={isOpen} onClose={Modals.close} />
        }
      />
      <Route
        path="/v2/patient/:id/eligibility/show"
        element={
          <EligibilityFormWrapper isOpen={isOpen} onClose={Modals.close} />
        }
      />
      <Route
        path="/v2/patient/:id/insurance/view"
        element={
          <InsuranceFormWrapper isOpen={isOpen} onClose={Modals.close} />
        }
      />
      <Route
        path="/v2/patient/:id/claim"
        element={
          <ClaimFormWrapper
            isEdit={false}
            isOpen={isOpen}
            onClose={Modals.close}
          />
        }
      />
      <Route
        path="/v2/patient/:id/claim/view"
        element={
          <ClaimFormWrapper
            isEdit={true}
            isOpen={isOpen}
            onClose={Modals.close}
          />
        }
      />
      <Route
        path="/v2/patient/:id/claim/edit"
        element={
          <ClaimFormWrapper
            isEdit={true}
            isOpen={isOpen}
            onClose={Modals.close}
          />
        }
      />
      <Route
        path="/v2/task/new"
        element={<TaskFormWrapper isOpen={isOpen} onClose={Modals.close} />}
      />
      <Route path="*" element={<FallbackRoute />} />
    </Routes>
  )
}

export default ModalRoutes
